import {
  AgentStarter,
  AgentFeedbackCategory,
  AgentFeedback,
  AgentFile,
  AgentMessage,
  AgentSession,
  Presentation,
} from './model';
import { readAgentStream } from './stream';
import type { AgentPageDecision, AgentPageObservation } from './page-task-protocol';

const MAX_COMPOSER_PREDICTION_LENGTH = 200;

export class AgentApiError extends Error {
  constructor(public status: number, public code?: number) {
    super('Agent request failed');
  }
}
export interface AgentTransport {
  host(): string;
  headers(): Promise<Record<string, string>>;
  onResponse(response: Response): void;
  log(event: string, fields: Record<string, string>): void;
}
export function createAgentApi(transport: AgentTransport) {
  const lifetime = new AbortController();
  async function request(path: string, method = 'GET', data?: unknown, signal?: AbortSignal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    lifetime.signal.addEventListener('abort', abort, { once: true });
    signal?.addEventListener('abort', abort, { once: true });
    if (lifetime.signal.aborted || signal?.aborted) abort();
    const started = Date.now();
    // Log a route template only, never credentials, query text, messages or signed URLs.
    const route = path
      .split('?')[0]
      .replace(/\/sessions\/[^/]+/, '/sessions/:id')
      .replace(/\/messages\/[^/]+/, '/messages/:id')
      .replace(/\/files\/[^/]+/, '/files/:id');
    transport.log('agent.req', { provider: 'lechat-agent', method, route });
    try {
      const headers = await transport.headers();
      const response = await fetch(
        `${transport.host().replace(/\/$/, '')}/lechat-agent/api/v1/agent${path}`,
        {
          method,
          headers: { ...headers, 'X-Client-App': 'fett', 'Content-Type': 'application/json' },
          body: data === undefined ? undefined : JSON.stringify(data),
          signal: controller.signal,
        },
      );
      transport.onResponse(response);
      transport.log('agent.resp', {
        provider: 'lechat-agent',
        method,
        route,
        status: String(response.status),
        durationMs: String(Date.now() - started),
        requestId: response.headers.get('x-request-id')?.slice(0, 128) || '',
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new AgentApiError(response.status, error.code);
      }
      return {
        response,
        release: () => {
          lifetime.signal.removeEventListener('abort', abort);
          signal?.removeEventListener('abort', abort);
        },
      };
    } catch (error) {
      lifetime.signal.removeEventListener('abort', abort);
      signal?.removeEventListener('abort', abort);
      transport.log('agent.resp.failed', {
        provider: 'lechat-agent',
        method,
        route,
        durationMs: String(Date.now() - started),
        aborted: String(controller.signal.aborted),
      });
      throw error;
    }
  }
  async function json<T>(path: string, method?: string, data?: unknown, signal?: AbortSignal): Promise<T> {
    const result = await request(path, method, data, signal);
    try {
      const text = await result.response.text();
      if (!text) return undefined as T;
      const value = JSON.parse(text);
      if (value && typeof value.code === 'number' && 'data' in value) {
        if (value.code !== 0) throw new AgentApiError(result.response.status, value.code);
        return value.data as T;
      }
      return value as T;
    } finally {
      result.release();
    }
  }
  const sessionPath = (id: string) => `/sessions/${encodeURIComponent(id)}`;
  return {
    dispose: () => lifetime.abort(),
    list: (page: number, keyword: string) =>
      json<{ sessions: AgentSession[]; total: number; page: number; pageSize: number }>(
        `/sessions?page=${page}&pageSize=30&keyword=${encodeURIComponent(keyword)}`,
      ),
    runtimeVersion: (id?: string) =>
      json<{
        current_image: string | null;
        target_image: string | null;
        is_latest: boolean | null;
        status: string;
      }>(id ? `${sessionPath(id)}/runtime-version` : '/sessions/runtime-version'),
    upgradeRuntime: (id?: string) =>
      json<{
        current_image: string | null;
        target_image: string | null;
        is_latest: boolean | null;
        status: string;
        outcome: 'upgraded' | 'alreadyLatest' | 'busy' | 'notAllocated';
      }>(id ? `${sessionPath(id)}/runtime-upgrade` : '/sessions/runtime-upgrade', 'POST'),
    create: () => json<AgentSession>('/sessions', 'POST'),
    session: (id: string) => json<AgentSession>(sessionPath(id)),
    messages: (id: string) => json<AgentMessage[]>(`${sessionPath(id)}/messages`),
    async predictComposer(id: string, messageId: string, locale: string) {
      const started = Date.now();
      transport.log('agent.composer-prediction.req', {
        provider: 'lechat-agent',
        method: 'POST /sessions/:id/composer-prediction',
        locale,
        messageIdChars: String(messageId.length),
      });
      const result = await json<{ message_id: string; suggestion: string | null }>(
        `${sessionPath(id)}/composer-prediction`,
        'POST',
        { message_id: messageId, locale },
      );
      const suggestion =
        result.message_id === messageId && typeof result.suggestion === 'string'
          ? result.suggestion.trim()
          : '';
      const valid = suggestion.length > 0 && suggestion.length <= MAX_COMPOSER_PREDICTION_LENGTH;
      // 只记录长度摘要，预测内容可能包含用户的对话信息。
      transport.log('agent.composer-prediction.resp', {
        provider: 'lechat-agent',
        method: 'POST /sessions/:id/composer-prediction',
        status: 'ok',
        hasSuggestion: String(valid),
        suggestionChars: String(suggestion.length),
        durationMs: String(Date.now() - started),
      });
      return valid ? suggestion : null;
    },
    prepare: (id: string) => json<AgentSession>(`${sessionPath(id)}/sandbox/prepare`, 'POST'),
    rename: (id: string, title: string) =>
      json<AgentSession>(`${sessionPath(id)}/title`, 'PATCH', { title }),
    pin: (id: string, pinned: boolean) =>
      json<AgentSession>(`${sessionPath(id)}/pin`, 'PATCH', { pinned }),
    remove: (id: string) => json<null>(sessionPath(id), 'DELETE'),
    read: (id: string) => json<null>(`${sessionPath(id)}/agent-result/read`, 'POST'),
    stop: (id: string, messageId: string) =>
      json<void>(`${sessionPath(id)}/messages/${encodeURIComponent(messageId)}/stop`, 'POST'),
    async decidePageAction(
      id: string,
      goal: string,
      observation: AgentPageObservation,
      signal?: AbortSignal,
    ) {
      const started = Date.now();
      transport.log('agent.page_task.req', {
        provider: 'lechat-agent',
        method: 'POST /page-task/step',
        scope: observation.scope,
        elementCount: String(observation.elements.length),
        goalChars: String(goal.length),
        revision: observation.revision.slice(0, 64),
      });
      try {
        const decision = await json<AgentPageDecision>(
          `${sessionPath(id)}/page-task/step`, 'POST', { goal, observation }, signal,
        );
        transport.log('agent.page_task.resp', {
          provider: 'lechat-agent',
          method: 'POST /page-task/step',
          status: 'ok',
          action: decision.action,
          durationMs: String(Date.now() - started),
          revision: observation.revision.slice(0, 64),
        });
        return decision;
      } catch (error) {
        transport.log('agent.page_task.resp', {
          provider: 'lechat-agent',
          method: 'POST /page-task/step',
          status: error instanceof AgentApiError ? String(error.status) : 'failed',
          durationMs: String(Date.now() - started),
          revision: observation.revision.slice(0, 64),
        });
        throw error;
      }
    },
    deny: (id: string, messageId: string, approvalId: string) =>
      json<void>(`${sessionPath(id)}/messages/${encodeURIComponent(messageId)}/approval`, 'POST', {
        approval_id: approvalId,
        choice: 'deny',
      }),
    starters: () => json<AgentStarter[]>('/starter-recommendations/resolve?kind=STARTER'),
    feedbackCategories: () =>
      json<AgentFeedbackCategory[]>('/feedback/categories?includeDisabled=false'),
    myFeedback: (id: string) =>
      json<AgentFeedback[]>(`/feedback/sessions/${encodeURIComponent(id)}/mine`),
    feedback: (
      messageId: string,
      rating: 'UP' | 'DOWN',
      comment = '',
      categoryIds: number[] = [],
    ) =>
      json<AgentFeedback>(`/feedback/messages/${encodeURIComponent(messageId)}`, 'POST', {
        rating,
        comment,
        categoryIds,
      }),
    files: (id: string) => json<AgentFile[]>(`${sessionPath(id)}/files?kind=GENERATED`),
    download: (id: string, fileId: string) =>
      json<{ url: string }>(`${sessionPath(id)}/files/${encodeURIComponent(fileId)}/download-url`),
    async upload(id: string, file: File) {
      const type = file.type || 'application/octet-stream';
      const ticket = await json<{
        file_id: string;
        upload_url: string;
        upload_method: string;
        headers: Record<string, string>;
      }>(`${sessionPath(id)}/files/upload-url`, 'POST', {
        file_name: file.name,
        content_type: type,
        size_bytes: file.size,
      });
      const started = Date.now();
      transport.log('agent.upload.req', {
        provider: 'object-storage',
        sizeBytes: String(file.size),
      });
      try {
        const response = await fetch(ticket.upload_url, {
          method: ticket.upload_method || 'PUT',
          headers: { 'Content-Type': type, ...ticket.headers },
          body: file,
          signal: lifetime.signal,
        });
        transport.log('agent.upload.resp', {
          provider: 'object-storage',
          status: String(response.status),
          durationMs: String(Date.now() - started),
        });
        if (!response.ok) throw new AgentApiError(response.status);
      } catch (error) {
        transport.log('agent.upload.resp.failed', {
          provider: 'object-storage',
          durationMs: String(Date.now() - started),
        });
        throw error;
      }
      return json<AgentFile>(
        `${sessionPath(id)}/files/${encodeURIComponent(ticket.file_id)}/complete`,
        'POST',
      );
    },
    async *stream(
      id: string,
      messageId: string,
      signal: AbortSignal,
      input?: {
        text: string;
        userMessageId?: string;
        guidance?: boolean;
        presentation: Presentation;
        locale: string;
        pageContext?: unknown;
        fileIds: string[];
      },
      after = 0,
    ) {
      const path = `${sessionPath(id)}/messages`;
      const result = await request(
        input ? path : `${path}/${encodeURIComponent(messageId)}/stream?after=${after}`,
        input ? 'POST' : 'GET',
        input
          ? {
              text: input.text,
              ...(input.userMessageId ? { user_message_id: input.userMessageId } : {}),
              agent_message_id: messageId,
              guidance: input.guidance === true,
              attachment_file_ids: input.fileIds,
              client_context: {
                presentation_mode: input.presentation,
                ui_locale: input.locale,
                page_context: input.pageContext,
              },
            }
          : undefined,
        signal,
      );
      try {
        const contentType = result.response.headers.get('content-type')?.toLowerCase() || '';
        if (contentType.includes('json')) {
          const payload = await result.response.json().catch(() => undefined);
          const code = typeof payload?.code === 'number' ? payload.code : undefined;
          transport.log('agent.stream.rejected', {
            provider: 'lechat-agent',
            status: String(result.response.status),
            code: code === undefined ? '' : String(code),
          });
          throw new AgentApiError(result.response.status, code);
        }
        if (!result.response.body) throw new Error('Agent stream unavailable');
        yield* readAgentStream(result.response.body);
      } finally {
        result.release();
      }
    },
  };
}
export type AgentApi = ReturnType<typeof createAgentApi>;
